import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { gradientAngle } from "@/utils/gradient";
import { CompanyDTO, Nullable } from "@shaarp/common";
import { useState } from "react";

/** Minimal company shape needed to render the avatar's logo cascade. */
export type AvatarCompanyData = Pick<
  CompanyDTO,
  "id" | "name" | "domain" | "logo" | "thumbnails"
>;

const LOGO_DEV_TOKEN = "pk_ZOfbu3pASUCK_8vMzJIuVg";

/**
 * Company avatar with cascading image fallback:
 * 1. logo.dev (from domain)
 * 2. Coresignal logo
 * 3. Google Maps thumbnail (Scrap.io)
 * 4. Google favicon from proprietary domain
 * 5. Gradient initial with app brand colors
 */
export default function AvatarCompany({
  company,
  className,
}: { company: Nullable<AvatarCompanyData> } & React.HTMLAttributes<HTMLDivElement>) {
  const [logoDevFailed, setLogoDevFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [faviconFailed, setFaviconFailed] = useState(false);

  const logoDevSrc = company?.domain
    ? `https://img.logo.dev/${company.domain}?token=${LOGO_DEV_TOKEN}&size=160&retina=true`
    : null;

  const logoSrc = company?.logo || "";
  const hasLogo = !!logoSrc;

  const thumbnailSrc = company?.thumbnails?.[0] || "";
  const hasThumbnail = !!thumbnailSrc;

  const faviconSrc = company?.domain
    ? `https://www.google.com/s2/favicons?sz=256&domain_url=${company.domain}`
    : null;

  const showLogoDev = logoDevSrc && !logoDevFailed;
  const showLogo = !showLogoDev && hasLogo && !logoFailed;
  const showThumbnail = !showLogoDev && !showLogo && hasThumbnail && !thumbnailFailed;
  const showFavicon = !showLogoDev && !showLogo && !showThumbnail && faviconSrc && !faviconFailed;

  return (
    <Avatar key={company?.id} className={cn(className)}>
      {!!company && showLogoDev && (
        <AvatarImage
          src={logoDevSrc}
          alt={company.name}
          onLoadingStatusChange={(status) => {
            if (status === "error") setLogoDevFailed(true);
          }}
        />
      )}
      {!!company && showLogo && (
        <AvatarImage
          src={logoSrc}
          alt={company.name}
          onLoadingStatusChange={(status) => {
            if (status === "error") setLogoFailed(true);
          }}
        />
      )}
      {!!company && showThumbnail && (
        <AvatarImage
          src={thumbnailSrc}
          alt={company.name}
          onLoadingStatusChange={(status) => {
            if (status === "error") setThumbnailFailed(true);
          }}
        />
      )}
      {!!company && showFavicon && (
        <AvatarImage
          src={faviconSrc}
          alt={company.name}
          onLoadingStatusChange={(status) => {
            if (status === "error") setFaviconFailed(true);
          }}
        />
      )}
      <AvatarFallback
        className="text-white font-normal text-xs"
        style={{
          background: `linear-gradient(${gradientAngle(company?.name || "company")}deg, hsl(248, 44%, 13%), hsl(250, 97%, 62%))`,
        }}
      >
        {company?.name?.charAt(0)?.toUpperCase() || "?"}
      </AvatarFallback>
    </Avatar>
  );
}
